import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import { useAuthContext } from "~/contexts/auth";
import { useCart } from "~/hooks/use-cart";
import useSubscriptions from "~/hooks/use-subscriptions";
import Editorials from "~/types/editorials";
import appRoutes from "~/utils/app-routes";
import { isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import MediaAsset from "../common/media-asset";
import styles from "./cart-kisses-info.module.scss";

type Props = {
  page: Editorials.CartPage | null;
  withImage?: boolean;
};

export default function CartKissesInfo({ page, withImage }: Props) {
  const { user, isAuthenticated } = useAuthContext();
  const { isUserSubscribedToLoyalty } = useSubscriptions();
  const cart = useCart();
  const t = useTranslations();
  const router = useRouter();

  const cartLoyaltyPoints = cart?.ShoppingCart?.TotalLoyaltyProgramDeposits?.[0]?.Points;

  return (
    <div className={classNames(styles.container, withImage ? styles.withImage : "")}>
      {withImage && (
        <div className={styles.imageContainer}>
          {isAuthenticated && page?.loggedLoyaltyProgramMedia && (
            <MediaAsset entry={page.loggedLoyaltyProgramMedia} noLazy={true} sizes="276px" />
          )}
          {!isAuthenticated && page?.guestLoyaltyProgramMedia && (
            <MediaAsset entry={page.guestLoyaltyProgramMedia} noLazy={true} sizes="276px" />
          )}
        </div>
      )}
      <div className={classNames(styles.textContainer, isAuthenticated && styles.logged)}>
        <p className={styles.text}>
          {isAuthenticated ? (
            <>
              <span className={styles.greeting}>
                {t("generic.cart.hiUser", {
                  userFirstName: user?.FirstName,
                })}{" "}
              </span>
              {isUserSubscribedToLoyalty
                ? cartLoyaltyPoints
                  ? t.rich("generic.withThisOrderYoureGaining", {
                      cartPoints: cartLoyaltyPoints,
                      kissesContainer: (chunks) => <span className={styles.gradient}>{chunks}</span>,
                    })
                  : t("generic.withThisOrderYoureGainingNoPoints")
                : t.rich("generic.withThisOrderYouWouldBeGaining", {
                    br: () => <br />,
                    strong: (chunks) => <span className={styles.strong}>{chunks}</span>,
                    myAccountLink: (chunks) => (
                      <Link className={styles.link} href={appRoutes.MYACCOUNT}>
                        {chunks}
                      </Link>
                    ),
                  })}
            </>
          ) : (
            t.rich("generic.withThisOrderYouWouldBeGainingAnonymous", {
              br: () => <br />,
              strong: (chunks) => <span className={styles.strong}>{chunks}</span>,
              signUpLink: (chunks) => (
                <Link className={styles.link} href={appRoutes.SIGN_UP}>
                  {chunks}
                </Link>
              ),
            })
          )}
        </p>
        {/* TODO: Hidden as per 2023/09/06 agreement. Since we don't have any information about the EVA discount associated to the current tier, and thus to the missing points required to go to the next level */}
        {/* <p className={styles.text}>
        {t.rich("generic.youreMissingPointsForNextLevel", {
            missingPoints: null,
            kissesContainer: (chunks) => <span className={styles.gradient}>{chunks}</span>
        })}
      </p> */}
        {page?.loyaltyProgramLink && isActiveEntry(page.loyaltyProgramLink) ? (
          <Link
            prefetch={false}
            className={styles.link}
            href={textUtils.sanitizeContentfulUrl(page.loyaltyProgramLink, router)}
            target={page.loyaltyProgramLink.fields.openOnANewTab ? "_blank" : undefined}
          >
            {page.loyaltyProgramLink.fields.text}
          </Link>
        ) : null}
      </div>
    </div>
  );
}
